/**
 * Theme Manager - Gestione temi
 * 
 * @since v3.1.0 - Global theme cache per ridurre richieste di rete
 */

import { TemplateEngine } from './TemplateEngine.js';
import { EVENTS } from '../constants/defaults.js';
import { dispatchCustomEvent } from '../utils/events.js';

// Cache globale condivisa tra tutte le istanze
const GLOBAL_THEME_CACHE = {
  css: new Map(),      // Map<themeName, cssText>
  html: new Map(),     // Map<themeName, htmlText>
  json: new Map(),     // Map<themeName, configObject>
  loading: new Map()   // Map<themeName, Promise> per evitare richieste duplicate simultanee
};

export class ThemeManager {
  constructor() {
    this.themesPath = 'themes/';
    this.currentTheme = 'default';
    this.debugMode = false;
    this.cacheBuster = null;
  }
  
  /**
   * Imposta path temi
   * @param {string} path - Path base
   */
  setThemesPath(path) {
    this.themesPath = path.endsWith('/') ? path : path + '/';
  }
  
  /**
   * Abilita modalità debug con cache-busting
   * @param {boolean} enabled - Abilita debug mode
   */
  setDebugMode(enabled) {
    this.debugMode = enabled;
    if (this.debugMode && !this.cacheBuster) {
      this.cacheBuster = Date.now();
      console.log('🐛 Debug Mode: Cache-busting abilitato per risorse tema (cacheBuster:', this.cacheBuster + ')');
    }
  }
  
  /**
   * Carica tema
   * @param {string} themeName - Nome tema
   * @param {Object} options - Opzioni di caricamento
   * @param {boolean} options.forceReload - Forza ricaricamento (ignora cache)
   * @param {boolean} options.debugMode - Abilita cache-busting
   * @returns {Promise<Object>} Config tema
   */
  async loadTheme(themeName, options = {}) {
    const { forceReload = false, debugMode = false } = options;
    
    // Abilita debug mode se richiesto
    if (debugMode) {
      this.setDebugMode(true);
    }
    
    // In debug mode, ricarica sempre per evitare cache del browser
    const shouldReload = this.debugMode || forceReload;
    const cacheKey = `${themeName}_${this.themesPath}`;
    
    // Controlla cache globale
    if (!shouldReload && GLOBAL_THEME_CACHE.json.has(cacheKey)) {
      if (this.debugMode) {
        console.log(`📦 Tema '${themeName}' preso dalla cache globale (richieste di rete: 0)`);
      }
      return GLOBAL_THEME_CACHE.json.get(cacheKey);
    }
    
    // Se c'è un caricamento in corso per questo tema, attendilo
    if (GLOBAL_THEME_CACHE.loading.has(cacheKey)) {
      if (this.debugMode) {
        console.log(`⏳ Tema '${themeName}' già in caricamento, attendo...`);
      }
      return await GLOBAL_THEME_CACHE.loading.get(cacheKey);
    }
    
    // Crea promise di caricamento e mettilo in cache
    const loadingPromise = this._loadThemeFromNetwork(themeName, cacheKey);
    GLOBAL_THEME_CACHE.loading.set(cacheKey, loadingPromise);
    
    try {
      const result = await loadingPromise;
      return result;
    } finally {
      // Rimuovi dalla loading map quando completato
      GLOBAL_THEME_CACHE.loading.delete(cacheKey);
    }
  }
  
  /**
   * Carica tema dalla rete e salva in cache globale
   * @private
   */
  async _loadThemeFromNetwork(themeName, cacheKey) {
    try {
      // Genera cache buster se in debug mode
      const cacheBuster = this.debugMode ? `?_=${this.cacheBuster}` : '';
      
      const logPrefix = this.debugMode ? '🐛' : '🌐';
      console.log(`${logPrefix} Caricamento tema '${themeName}' dalla rete...`);
      
      // Carica theme.json
      const configResponse = await fetch(`${this.themesPath}${themeName}/theme.json${cacheBuster}`);
      if (!configResponse.ok) {
        throw new Error(`Tema '${themeName}' non trovato`);
      }
      
      const themeConfig = await configResponse.json();
      GLOBAL_THEME_CACHE.json.set(cacheKey, themeConfig);
      
      // Carica theme.css
      const cssResponse = await fetch(`${this.themesPath}${themeName}/theme.css${cacheBuster}`);
      if (!cssResponse.ok) {
        throw new Error(`CSS del tema '${themeName}' non trovato`);
      }
      
      const themeCSS = await cssResponse.text();
      GLOBAL_THEME_CACHE.css.set(cacheKey, themeCSS);
      
      // Carica template.html (opzionale)
      try {
        const templateResponse = await fetch(`${this.themesPath}${themeName}/template.html${cacheBuster}`);
        if (templateResponse.ok) {
          const themeTemplate = await templateResponse.text();
          GLOBAL_THEME_CACHE.html.set(cacheKey, themeTemplate);
          themeConfig.template = themeTemplate;
        }
      } catch (e) {
        // Template opzionale
      }
      
      const finalLogPrefix = this.debugMode ? '🐛' : '✅';
      console.log(`${finalLogPrefix} Tema '${themeName}' caricato e salvato in cache globale${this.debugMode ? ' (no-cache)' : ''}`);
      return themeConfig;
      
    } catch (error) {
      // Rimuovi dalla cache in caso di errore
      GLOBAL_THEME_CACHE.json.delete(cacheKey);
      GLOBAL_THEME_CACHE.css.delete(cacheKey);
      GLOBAL_THEME_CACHE.html.delete(cacheKey);
      
      console.error(`❌ Errore caricamento tema '${themeName}':`, error);
      
      if (themeName !== 'default') {
        return await this.loadTheme('default');
      }
      
      throw error;
    }
  }
  
  /**
   * Applica tema a shadow DOM
   * @param {ShadowRoot} shadowRoot - Shadow root
   * @param {string} themeName - Nome tema
   */
  applyTheme(shadowRoot, themeName) {
    const cacheKey = `${themeName}_${this.themesPath}`;
    const themeCSS = GLOBAL_THEME_CACHE.css.get(cacheKey);
    const themeConfig = GLOBAL_THEME_CACHE.json.get(cacheKey);
    
    if (!themeCSS || !themeConfig) {
      console.error(`❌ Tema '${themeName}' non caricato o non in cache`);
      return;
    }
    
    // Rimuovi tema precedente
    const existingStyle = shadowRoot.querySelector('#sasha-theme-styles');
    if (existingStyle) {
      existingStyle.remove();
    }
    
    // Aggiungi CSS tema dalla cache globale
    const styleEl = document.createElement('style');
    styleEl.id = 'sasha-theme-styles';
    styleEl.textContent = themeCSS;
    shadowRoot.appendChild(styleEl);
    
    // NOTA v2.4.0+: Le CSS custom properties sono definite nel theme.css
    // Non vengono più applicate via JavaScript. Il metodo applyThemeVariables è stato rimosso.
    // Tutti i colori e stili devono essere nel file theme.css del tema.
    
    this.currentTheme = themeName;
  }
  
  /**
   * Ottieni config tema
   * @param {string} themeName - Nome tema
   * @returns {Object|null} Config tema
   */
  getThemeConfig(themeName) {
    const cacheKey = `${themeName}_${this.themesPath}`;
    return GLOBAL_THEME_CACHE.json.get(cacheKey) || null;
  }
  
  /**
   * Render template con placeholder
   * @param {string} themeName - Nome tema
   * @param {Object} data - Dati per placeholder
   * @returns {string} HTML renderizzato
   */
  renderTemplate(themeName, data) {
    const cacheKey = `${themeName}_${this.themesPath}`;
    const themeConfig = GLOBAL_THEME_CACHE.json.get(cacheKey);
    
    if (!themeConfig || !themeConfig.template) {
      return null;
    }
    
    return TemplateEngine.render(themeConfig.template, data);
  }
  
  /**
   * Lista temi caricati
   * @returns {Array<string>} Nomi temi
   */
  getLoadedThemes() {
    // Estrai nomi temi dalla cache globale
    const themeNames = new Set();
    for (const cacheKey of GLOBAL_THEME_CACHE.json.keys()) {
      const themeName = cacheKey.split('_')[0];
      themeNames.add(themeName);
    }
    return Array.from(themeNames);
  }
  
  /**
   * Pulisci cache globale (utility per debug)
   * @param {string} themeName - Nome tema da rimuovere (opzionale)
   */
  clearCache(themeName = null) {
    if (themeName) {
      const cacheKey = `${themeName}_${this.themesPath}`;
      GLOBAL_THEME_CACHE.css.delete(cacheKey);
      GLOBAL_THEME_CACHE.html.delete(cacheKey);
      GLOBAL_THEME_CACHE.json.delete(cacheKey);
      console.log(`🗑️ Cache tema '${themeName}' pulita`);
    } else {
      GLOBAL_THEME_CACHE.css.clear();
      GLOBAL_THEME_CACHE.html.clear();
      GLOBAL_THEME_CACHE.json.clear();
      GLOBAL_THEME_CACHE.loading.clear();
      console.log('🗑️ Cache globale temi completamente pulita');
    }
  }
}

// Istanza globale (per compatibilità)
if (typeof window !== 'undefined') {
  window.SashaThemeManager = new ThemeManager();
  dispatchCustomEvent(window, EVENTS.THEME_MANAGER_READY);
  window.SashaThemeManagerReady = true;
}

